import React, { useState } from "react";
import { ArrowRight, Building2, GraduationCap, Mic, HandshakeIcon, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHero, SectionHeader } from "@/components/Bits";
import { LIFESTYLE } from "@/lib/brand";

const interests = [
  "Corporate Partnership",
  "Community Collaboration",
  "Sponsorship",
  "Educational Initiative",
  "Workforce Development Program",
  "Speaking Engagement",
];

const opportunities = [
  { icon: Building2, title: "Corporate Partnerships", body: "Co-design programming that supports your workforce's well-being and professional growth." },
  { icon: HandshakeIcon, title: "Community Collaborations", body: "Bring strategic education to communities advancing economic mobility." },
  { icon: GraduationCap, title: "Educational Initiatives", body: "Partner with universities, foundations, and educators to expand digital literacy." },
  { icon: Mic, title: "Speaking Engagements", body: "Invite Dr. Foster to your conference, summit, or executive convening." },
];

const Partnerships = () => {
  const [form, setForm] = useState({
    organization: "",
    name: "",
    email: "",
    interest: interests[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partnerships", form);
      toast.success("Inquiry received — our team will reach out shortly.");
      setForm({ organization: "", name: "", email: "", interest: interests[0], message: "" });
    } catch (err) {
      toast.error("Could not submit your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-partnerships">
      <PageHero
        overline="Partnerships"
        title="Partner with The Exit Strategy Society."
        subtitle="We collaborate with corporations, foundations, community organizations, and educational institutions to expand access to economic empowerment."
        image={LIFESTYLE.meeting}
      />

      <section className="ess-section bg-ess-paper">
        <div className="ess-container">
          <SectionHeader
            overline="Opportunities"
            title="Six ways to partner with us."
            align="center"
            maxWidth="max-w-2xl mx-auto"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ess-blush mt-12 border border-ess-blush">
            {opportunities.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white p-8">
                <div className="w-12 h-12 border border-ess-gold/40 flex items-center justify-center text-ess-gold-deep">
                  <Icon size={20} strokeWidth={1.4} />
                </div>
                <h3 className="font-display text-xl mt-5 text-ess-emerald">{title}</h3>
                <p className="text-ess-slate text-sm mt-3 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ess-section bg-ess-blush">
        <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="ess-overline">Become a Partner</p>
            <h2 className="font-display text-4xl md:text-5xl text-ess-emerald mt-4 leading-tight">
              Tell us how you'd like to collaborate.
            </h2>
            <p className="text-ess-slate mt-5 leading-relaxed">
              Share a few details and our team will respond with next steps —
              typically within two business days.
            </p>
          </div>
          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="partnership-form"
          >
            <div className="md:col-span-2">
              <label className="ess-overline">Organization</label>
              <input
                required
                value={form.organization}
                onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                data-testid="p-organization"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              />
            </div>
            <div>
              <label className="ess-overline">Your Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="p-name"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              />
            </div>
            <div>
              <label className="ess-overline">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="p-email"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="ess-overline">Area of Interest</label>
              <select
                value={form.interest}
                onChange={(e) => setForm((f) => ({ ...f, interest: e.target.value }))}
                data-testid="p-interest"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none"
              >
                {interests.map((i) => (
                  <option key={i}>{i}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="ess-overline">Message</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                data-testid="p-message"
                className="mt-2 w-full bg-ess-paper px-4 py-3 border border-ess-blush-deep focus:border-ess-emerald outline-none resize-none"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="p-submit"
                className="bg-ess-emerald text-white px-7 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-ess-emerald-dark transition disabled:opacity-60 inline-flex items-center gap-3"
              >
                {submitting ? "Sending…" : "Submit Inquiry"} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Partnerships;
